import * as React from 'react';
import { useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@klareospace/pharos-lib-web-utils';
import { useIsMobile } from '@klareospace/pharos-lib-web-utils';
import { SEARCH_DEBOUNCE_MS, useDebouncedValue } from '@klareospace/pharos-lib-front-utils';
import { useTranslation } from 'react-i18next';
import type { CurrentUserResponse } from '@klareospace/pharos-lib-types';
import { useMasterContext } from '&/master';
import type { PanelView } from './main-selector-dropdown-content';
import { MainSelectorDropdownContent } from './main-selector-dropdown-content';

// ─── Props ────────────────────────────────────────────────────────────────────

interface MainSelectorDropdownProps {
  backendUser: CurrentUserResponse | null;
  canReadSites: boolean;
  canReadSpaces: boolean;
  children: React.ReactNode;
  hasMultipleSpaces: boolean;
  singleSpaceId: string | null;
  spaceName?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface InitialDropdownState {
  browsedSpaceId: string | null;
  browsedSpaceName: string | null;
  panel: PanelView;
}

interface ResolveInitialDropdownStateParams {
  currentSpaceId: string | null;
  isSingleSpaceImposed: boolean;
  resolvedSpaceName: string | null;
  singleSpaceId: string | null;
}

function resolveInitialDropdownState({
  currentSpaceId,
  isSingleSpaceImposed,
  resolvedSpaceName,
  singleSpaceId,
}: ResolveInitialDropdownStateParams): InitialDropdownState {
  if (isSingleSpaceImposed) {
    return { browsedSpaceId: singleSpaceId, browsedSpaceName: resolvedSpaceName, panel: 'sites' };
  }
  if (currentSpaceId) {
    return { browsedSpaceId: currentSpaceId, browsedSpaceName: resolvedSpaceName, panel: 'sites' };
  }
  return { browsedSpaceId: null, browsedSpaceName: null, panel: 'spaces' };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function MainSelectorDropdown({
  backendUser,
  canReadSites,
  canReadSpaces,
  children,
  hasMultipleSpaces,
  singleSpaceId,
  spaceName,
}: MainSelectorDropdownProps): React.JSX.Element {
  const { t } = useTranslation('common');
  const isMobile = useIsMobile();
  const { spaceId: currentSpaceId } = useMasterContext();

  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState<PanelView>('spaces');
  const [browsedSpaceId, setBrowsedSpaceId] = useState<string | null>(null);
  const [browsedSpaceName, setBrowsedSpaceName] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);

  // Single-space imposed: the user only has one space, so the dropdown always
  // opens directly to the sites panel with no ability to go back to spaces.
  const isSingleSpaceImposed = !hasMultipleSpaces && !!singleSpaceId;

  const handleOpenChange = (nextOpen: boolean): void => {
    setOpen(nextOpen);
    if (nextOpen) {
      // Reset search on open
      setSearchQuery('');
      const resolvedSpaceName = spaceName ?? null;
      const initial = resolveInitialDropdownState({
        currentSpaceId,
        isSingleSpaceImposed,
        resolvedSpaceName,
        singleSpaceId,
      });
      setPanel(initial.panel);
      setBrowsedSpaceId(initial.browsedSpaceId);
      setBrowsedSpaceName(initial.browsedSpaceName);
    }
    // Don't reset state on close — let it hold until next open to avoid flash during close animation
  };

  const handleBrowseSpace = (spaceId: string, spaceName: string): void => {
    // Clear the search query so the sites panel becomes visible instead of the search results.
    setSearchQuery('');
    setBrowsedSpaceId(spaceId);
    setBrowsedSpaceName(spaceName);
    setPanel('sites');
  };

  const handleGoBackToSpaces = (): void => {
    setPanel('spaces');
  };

  const handleClose = (): void => {
    handleOpenChange(false);
  };

  const contentProps = {
    backendUser,
    browsedSpaceId,
    browsedSpaceName,
    canReadSites,
    canReadSpaces,
    canSelectSpace: canReadSpaces,
    debouncedQuery,
    isSingleSpaceImposed,
    onBrowseSpace: handleBrowseSpace,
    onClose: handleClose,
    onGoBackToSpaces: handleGoBackToSpaces,
    onSearchChange: setSearchQuery,
    panel,
    searchQuery,
  };

  if (isMobile) {
    return (
      <Drawer onOpenChange={handleOpenChange} open={open}>
        <DrawerTrigger asChild>{children}</DrawerTrigger>
        <DrawerContent className="max-h-[85vh]">
          <DrawerTitle className="sr-only">{t('mainSelector.drawerTitle')}</DrawerTitle>
          <MainSelectorDropdownContent {...contentProps} />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover onOpenChange={handleOpenChange} open={open}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="start" alignOffset={-4} className="w-[360px] p-0 overflow-hidden" sideOffset={8}>
        <MainSelectorDropdownContent {...contentProps} />
      </PopoverContent>
    </Popover>
  );
}
